// @flow

import React from 'react';
import { connect } from 'react-redux';
import changeCarouselSlotIndex from 'app/common/redux/actions/pin/carousel';
import enablePointerEvents from 'app/common/react/components/PinRep/utils/enablePointerEvents';
import getCarouselData from 'app/common/react/components/common/Carousel/utils/getCarouselData';
import i18n from 'app/common/lib/i18n';
import { getCommerceAuxData } from 'app/common/lib/CommerceUtil';
import { track } from 'app/common/lib/react/analytics';
import { type Dispatch } from 'redux';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import { Box, Icon, Touchable } from 'gestalt';

type SwipeDirection = 'left' | 'right';

type SwipeArrowProps = {|
  direction: SwipeDirection,
  swipeHandler: Function,
|};

function SwipeArrow({ direction, swipeHandler }: SwipeArrowProps) {
  const icons = {
    left: 'arrow-back',
    right: 'arrow-forward',
  };
  const accessibilityLabels = {
    left: i18n._('View Previous', 'Button to advance carousel to the previous image'),
    right: i18n._('View Next', 'Button to advance carousel to the next image'),
  };
  return (
    <Box position="absolute" bottom top {...{ [direction]: true }}>
      <Touchable fullWidth fullHeight onTouch={swipeHandler}>
        <Box
          alignItems="center"
          display="flex"
          height="100%"
          justifyContent="center"
          padding={2}
          width="100%"
        >
          <Icon
            accessibilityLabel={accessibilityLabels[direction]}
            color="white"
            icon={icons[direction]}
          />
        </Box>
      </Touchable>
    </Box>
  );
}

type ReduxDispatchProps = {|
  changeCarouselSlotIndex: (pinId: string, index: number) => void,
|};

type OwnProps = {|
  pin: Pin,
|};

export type Props = {|
  ...ReduxDispatchProps,
  ...OwnProps,
|};

export const CarouselSliders = ({ pin, changeCarouselSlotIndex }: Props) => {
  const { componentType, viewParameter, viewType } = useTrackingParameters();

  const carouselData = getCarouselData(pin);
  if (!carouselData) {
    return null;
  }

  const { id: pinId = '' } = pin;
  const { carousel_slots, index, id: carouselId } = carouselData;

  const logSwipe = (currentIndex: number, nextIndex: number) => {
    track(PCONST.thrift.EventType.SWIPE, {
      objectId: pinId,
      component: componentType,
      view: viewType,
      viewParameter,
      eventData: {
        pinCarouselSlotEventData: {
          carouselSlotId: carousel_slots[currentIndex] && Number(carousel_slots[currentIndex].id),
          carouselDataId: Number(carouselId),
          carouselSlotIndex: currentIndex,
          toCarouselSlotIndex: nextIndex,
        },
      },
      ...(getCommerceAuxData(pin) || {}),
    });
  };

  function buildSwipeHandler(direction: SwipeDirection) {
    return () => {
      const nextIndex = direction === 'left' ? index - 1 : index + 1;
      changeCarouselSlotIndex(pinId, nextIndex);
      logSwipe(index, nextIndex);
    };
  }

  return (
    <Box height="100%" position="relative" width="100%">
      {/* LEFT ARROW */}
      {index > 0 &&
        enablePointerEvents(
          <SwipeArrow direction="left" swipeHandler={buildSwipeHandler('left')} />,
        )}

      {/* RIGHT ARROW */}
      {index < carousel_slots.length - 1 &&
        enablePointerEvents(
          <SwipeArrow direction="right" swipeHandler={buildSwipeHandler('right')} />,
        )}
    </Box>
  );
};

function mapDispatchToProps(dispatch: Dispatch<any>): ReduxDispatchProps {
  return {
    changeCarouselSlotIndex: (pinId, newIndex) =>
      dispatch(changeCarouselSlotIndex(pinId, newIndex)),
  };
}

export default connect<Props, OwnProps, _, ReduxDispatchProps, _, Dispatch<any>>(
  null,
  mapDispatchToProps,
)(CarouselSliders);
